/**
 * 2D top-down plan symbols for prop families (Konva nodes). Design bundle ONLY — no three/r3f.
 *
 * Each builder receives the fully-resolved ParamValues and a pixel-constant stroke helper.
 * Variant families (sofa shape, bed size) branch here. Convention: centered on origin,
 * width=x axis, depth=y axis, rotation-0 faces +plan-y (front toward canvas bottom).
 */
import { Fragment, type ReactNode } from 'react'
import { Circle, Ellipse, Line, Rect } from 'react-konva'
import type { ParamValues } from '@/model/params'
import { pBool, pNum, pStr } from '@/model/params'
import type { StyleId } from '@/model/types'
import { propDimensions } from './catalog'

export const PROP_STROKE = '#c2cadb'
const FILL = 'rgba(186, 196, 216, 0.14)'
const ACCENT = 'rgba(186, 196, 216, 0.26)'

interface FP {
  w: number; d: number; hw: number; hd: number
  sw: (n: number) => number
}

function mkFP(w: number, d: number, scale: number): FP {
  return { w, d, hw: w / 2, hd: d / 2, sw: (n) => n / scale }
}

function body({ w, d, hw, hd, sw }: FP, opts: { fill?: string; radius?: number } = {}) {
  return (
    <Rect
      x={-hw} y={-hd} width={w} height={d}
      cornerRadius={opts.radius ?? Math.min(w, d) * 0.12}
      fill={opts.fill ?? FILL}
      stroke={PROP_STROKE}
      strokeWidth={sw(1.6)}
    />
  )
}

type FamilyBuilder = (params: ParamValues, scale: number, _style: StyleId) => ReactNode

const BUILDERS: Record<string, FamilyBuilder> = {

  // ── Bed ──────────────────────────────────────────────────────────────────
  bed: (params, scale) => {
    const { width: w, depth: d } = propDimensions('bed', params)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        {body(f, { radius: Math.min(w, d) * 0.06 })}
        <Rect x={-hw} y={-hd} width={w} height={d * 0.04} fill={PROP_STROKE} />
        <Rect x={-hw + w * 0.08} y={-hd + d * 0.06} width={w * 0.36} height={d * 0.16} cornerRadius={sw(2)} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Rect x={hw - w * 0.44} y={-hd + d * 0.06} width={w * 0.36} height={d * 0.16} cornerRadius={sw(2)} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Line points={[-hw, -hd + d * 0.34, hw, -hd + d * 0.34]} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        {/* bunk: second mattress indicator */}
        {pBool(params, 'bunk') && (
          <Rect x={-hw + w * 0.04} y={hd - d * 0.14} width={w * 0.92} height={d * 0.1} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        )}
      </Fragment>
    )
  },

  // ── Sofa (branches on shape) ──────────────────────────────────────────────
  sofa: (params, scale) => {
    const shape = pStr(params, 'shape', 'straight')
    const { width: w, depth: d } = propDimensions('sofa', params)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f

    if (shape === 'L') {
      // L-shape: main seat + one side return
      const rd = d * 0.45
      return (
        <Fragment>
          {/* main seat */}
          <Rect x={-hw} y={-hd} width={w} height={d - rd} cornerRadius={Math.min(w, d) * 0.08}
            fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          {/* side return at +x */}
          <Rect x={hw - w * 0.35} y={-hd + d - rd} width={w * 0.35} height={rd} cornerRadius={Math.min(w, rd) * 0.08}
            fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          {/* backrest */}
          <Rect x={-hw} y={-hd} width={w} height={d * 0.22} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        </Fragment>
      )
    }

    if (shape === 'U') {
      // U-shape: main seat + both side returns
      const rd = d * 0.38
      const rw = w * 0.3
      return (
        <Fragment>
          {/* main seat */}
          <Rect x={-hw + rw} y={-hd} width={w - rw * 2} height={d} cornerRadius={d * 0.08}
            fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          {/* left return */}
          <Rect x={-hw} y={-hd + d - rd} width={rw} height={rd} cornerRadius={Math.min(rw, rd) * 0.08}
            fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          {/* right return */}
          <Rect x={hw - rw} y={-hd + d - rd} width={rw} height={rd} cornerRadius={Math.min(rw, rd) * 0.08}
            fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          {/* backrest */}
          <Rect x={-hw + rw} y={-hd} width={w - rw * 2} height={d * 0.22} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        </Fragment>
      )
    }

    // straight
    return (
      <Fragment>
        {body(f)}
        <Rect x={-hw} y={-hd} width={w} height={d * 0.22} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Rect x={-hw} y={-hd} width={w * 0.12} height={d} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Rect x={hw - w * 0.12} y={-hd} width={w * 0.12} height={d} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Line points={[0, -hd + d * 0.22, 0, hd]} stroke={PROP_STROKE} strokeWidth={sw(1)} />
      </Fragment>
    )
  },

  // ── Chair ──────────────────────────────────────────────────────────────────
  chair: (params, scale) => {
    const w = pNum(params, 'width', 1.8)
    const d = pNum(params, 'depth', 1.8)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        {body(f)}
        <Rect x={-hw} y={-hd} width={w} height={d * 0.22} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
      </Fragment>
    )
  },

  // ── Table ─────────────────────────────────────────────────────────────────
  table: (params, scale) => {
    const w = pNum(params, 'width', 4)
    const d = pNum(params, 'depth', 2.5)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        {body(f)}
        <Rect x={-hw + w * 0.12} y={-hd + d * 0.12} width={w * 0.76} height={d * 0.76}
          stroke={PROP_STROKE} strokeWidth={sw(1)} dash={[0.3, 0.25]} />
      </Fragment>
    )
  },

  // ── Wardrobe ─────────────────────────────────────────────────────────────
  wardrobe: (params, scale) => {
    const w = pNum(params, 'width', 4)
    const d = pNum(params, 'depth', 2)
    const doors = pStr(params, 'doors', '2door')
    const f = mkFP(w, d, scale)
    const { hd, sw } = f
    const seams = doors === '3door' ? [-w / 3, 0, w / 3] : [0]
    return (
      <Fragment>
        {body(f, { radius: 0 })}
        {seams.map((sx, i) => (
          <Line key={i} points={[sx, -hd, sx, hd]} stroke={PROP_STROKE} strokeWidth={sw(1.2)} />
        ))}
        {seams.map((sx, i) => (
          <Circle key={`h${i}`} x={sx - d * 0.12} y={0} radius={sw(2)} fill={PROP_STROKE} />
        ))}
      </Fragment>
    )
  },

  // ── TV ────────────────────────────────────────────────────────────────────
  tv: (params, scale) => {
    const { width: w, depth: d } = propDimensions('tv', params)
    const mount = pStr(params, 'mount', 'stand')
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    if (mount === 'projector') {
      return (
        <Fragment>
          {/* projector box */}
          <Rect x={-hw} y={-hd} width={w} height={d} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
          <Line points={[-hw, hd, hw, hd, 0, hd + d * 2, -hw, hd]} stroke={PROP_STROKE} strokeWidth={sw(1)} dash={[0.3, 0.3]} />
        </Fragment>
      )
    }
    return (
      <Fragment>
        <Rect x={-hw} y={-hd} width={w} height={d * 0.5} fill={PROP_STROKE} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Rect x={-w * 0.16} y={-hd + d * 0.5} width={w * 0.32} height={d * 0.5} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
      </Fragment>
    )
  },

  // ── Toilet ────────────────────────────────────────────────────────────────
  toilet: (params, scale) => {
    const w = pNum(params, 'width', 1.5)
    const d = pNum(params, 'depth', 2.3)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        <Rect x={-hw} y={-hd} width={w} height={d * 0.28} cornerRadius={sw(2)} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1.4)} />
        <Ellipse x={0} y={hd - d * 0.34} radiusX={w * 0.42} radiusY={d * 0.32} fill={FILL} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
        <Ellipse x={0} y={hd - d * 0.34} radiusX={w * 0.26} radiusY={d * 0.2} stroke={PROP_STROKE} strokeWidth={sw(1)} />
      </Fragment>
    )
  },

  // ── Sink (branches on size) ───────────────────────────────────────────────
  sink: (params, scale) => {
    const { width: w, depth: d } = propDimensions('sink', params)
    const f = mkFP(w, d, scale)
    const { hd, sw } = f
    return (
      <Fragment>
        {body(f, { radius: Math.min(w, d) * 0.18 })}
        <Ellipse x={0} y={d * 0.05} radiusX={w * 0.32} radiusY={d * 0.3} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1.2)} />
        <Circle x={0} y={-hd + d * 0.18} radius={sw(2.4)} fill={PROP_STROKE} />
      </Fragment>
    )
  },

  // ── Shower ────────────────────────────────────────────────────────────────
  shower: (params, scale) => {
    const w = pNum(params, 'width', 3)
    const d = pNum(params, 'depth', 3)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        {body(f, { radius: 0 })}
        <Circle x={0} y={0} radius={Math.min(w, d) * 0.08} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Line points={[-hw, -hd, hw, hd]} stroke={PROP_STROKE} strokeWidth={sw(0.8)} dash={[0.3, 0.3]} />
        <Line points={[hw, -hd, -hw, hd]} stroke={PROP_STROKE} strokeWidth={sw(0.8)} dash={[0.3, 0.3]} />
        <Rect x={-hw} y={hd - d * 0.06} width={w} height={d * 0.06} fill={PROP_STROKE} />
      </Fragment>
    )
  },

  // ── Kitchen counter ───────────────────────────────────────────────────────
  kitchenCounter: (params, scale) => {
    const w = pNum(params, 'width', 6)
    const d = pNum(params, 'depth', 2)
    const f = mkFP(w, d, scale)
    const { hw, hd, sw } = f
    return (
      <Fragment>
        {body(f, { radius: 0 })}
        <Rect x={-hw} y={-hd} width={w} height={d * 0.12} fill={PROP_STROKE} />
        <Rect x={-w * 0.3} y={-hd + d * 0.28} width={w * 0.26} height={d * 0.5} cornerRadius={sw(2)} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1.2)} />
        <Circle x={-w * 0.17} y={-hd + d * 0.22} radius={sw(2)} fill={PROP_STROKE} />
      </Fragment>
    )
  },

  // ── Car (branches on style) ───────────────────────────────────────────────
  car: (params, scale) => {
    const w = pNum(params, 'width', 6)
    const d = pNum(params, 'depth', 14)
    const f = mkFP(w, d, scale)
    const { hw, sw } = f
    return (
      <Fragment>
        {body(f, { radius: Math.min(w, d) * 0.18 })}
        <Rect x={-hw + w * 0.08} y={-d * 0.16} width={w * 0.84} height={d * 0.36} cornerRadius={sw(2)} fill={ACCENT} stroke={PROP_STROKE} strokeWidth={sw(1)} />
        <Line points={[-hw + w * 0.08, -d * 0.16, hw - w * 0.08, -d * 0.16]} stroke={PROP_STROKE} strokeWidth={sw(1.2)} />
        <Line points={[-hw + w * 0.08, d * 0.2, hw - w * 0.08, d * 0.2]} stroke={PROP_STROKE} strokeWidth={sw(1.2)} />
      </Fragment>
    )
  },

  // ── Bike ──────────────────────────────────────────────────────────────────
  bike: (params, scale) => {
    const w = pNum(params, 'width', 2.5)
    const d = pNum(params, 'depth', 6)
    const f = mkFP(w, d, scale)
    const { hd, sw } = f
    const r = w * 0.42
    return (
      <Fragment>
        <Circle x={0} y={-hd + r} radius={r} stroke={PROP_STROKE} strokeWidth={sw(1.6)} fill={FILL} />
        <Circle x={0} y={hd - r} radius={r} stroke={PROP_STROKE} strokeWidth={sw(1.6)} fill={FILL} />
        <Line points={[0, -hd + r, 0, hd - r]} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
        <Line points={[-w * 0.3, -hd + r, w * 0.3, -hd + r]} stroke={PROP_STROKE} strokeWidth={sw(1.6)} />
      </Fragment>
    )
  },
}

/**
 * Render the top-down footprint for a prop given its familyId and resolved params.
 * Drawn in local prop space (origin = center, no rotation applied).
 */
export function FamilyFootprint2D({
  familyId,
  params,
  scale,
  style = 'standard',
}: {
  familyId: string
  params: ParamValues
  scale: number
  style?: StyleId
}) {
  const build = BUILDERS[familyId]
  if (build) return <>{build(params, scale, style)}</>
  // Fallback: a plain rect at the family's computed dimensions
  const { width: w, depth: d } = propDimensions(familyId, params)
  const f = mkFP(w, d, scale)
  return <>{body(f)}</>
}
